import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DataTable, { ColDef } from '../util/DataTable';

interface QueuePayload {
  // pendingReport
  supervisorId?: number;
  employeeName?: string;
  weekStart?: string;
  weekEnd?: string;
  // rejectedReport
  userId?: number;
  reportDate?: string;
  startTime?: string;
  endTime?: string;
  tasks?: string;
  categoryName?: string;
  // common
  fromEmail?: string;
  toEmail?: string;
  toName?: string;
}

interface QueueItem {
  id: number;
  type: string;
  status: 'pending' | 'sent' | 'failed';
  attempts: number;
  maxAttempts: number;
  lastError: string | null;
  nextAttemptAt: string | null;
  createdAt: string;
  updatedAt: string;
  payload: QueuePayload;
}

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  pending: { bg: 'rgba(217,119,6,.12)', color: '#b45309', label: 'Pending' },
  sent:    { bg: 'rgba(5,150,105,.12)', color: '#059669', label: 'Sent'    },
  failed:  { bg: 'rgba(220,38,38,.12)', color: '#dc2626', label: 'Failed'  },
};

const TYPE_LABEL: Record<string, string> = {
  pendingReport:  'Report submitted',
  rejectedReport: 'Report rejected',
  invite_user:    'User invited',
};

function fmt(d: string) {
  return new Date(d).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

// ── Queue tab ─────────────────────────────────────────────────────────────────

function QueueTab() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [retryingAll, setRetryingAll] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('mail-queue/queue');
      setItems(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRetry = async (id: number) => {
    setRetrying(id);
    try {
      await axios.put(`mail-queue/${id}/retry`);
      await load();
    } catch {}
    setRetrying(null);
  };

  const handleDelete = async (id: number) => {
    setDeleting(id);
    try {
      await axios.delete(`mail-queue/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {}
    setDeleting(null);
  };

  const handleRetryAll = async () => {
    setRetryingAll(true);
    try {
      await axios.post('mail-queue/retry-all');
      await load();
    } catch {}
    setRetryingAll(false);
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) return <Spinner />;

  const pending = items.filter(i => i.status === 'pending').length;
  const failed  = items.filter(i => i.status === 'failed').length;

  return (
    <div className="flex flex-col gap-[10px]">
      {/* Stats */}
      <div className="flex gap-[8px]">
        <StatCard label="Pending" value={pending} color="#b45309" />
        <StatCard label="Failed"  value={failed}  color="#dc2626" />
        <StatCard label="Total"   value={items.length} color="var(--text-color)" />
      </div>

      <div className="flex items-center gap-[6px] justify-end">
        {failed > 0 && (
          <button
            type="button"
            onClick={handleRetryAll}
            disabled={retryingAll}
            className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[4px] px-[12px] rounded-[5px] text-[12px] cursor-pointer hover:bg-[var(--btn-user-hover)] disabled:opacity-50">
            {retryingAll ? '…' : `Retry all failed (${failed})`}
          </button>
        )}
        <button type="button" onClick={load} className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[4px] px-[12px] rounded-[5px] text-[12px] cursor-pointer hover:bg-[var(--btn-user-hover)]">
          Refresh
        </button>
      </div>

      {items.length === 0 ? (
        <Empty text="Queue is empty" />
      ) : (
        <div className="overflow-x-auto rounded-[6px]" style={{ border: '1px solid var(--border-input)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--background-color)', borderBottom: '1px solid var(--border-input)' }}>
                <Th>Type / Recipient</Th>
                <Th>Status</Th>
                <Th>Attempts</Th>
                <Th>Created</Th>
                <Th>Next try</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const st = STATUS_STYLE[item.status] ?? STATUS_STYLE.pending;
                const isRetrying = retrying === item.id;
                const isDeleting = deleting === item.id;
                const isExpanded = expanded.has(item.id);
                const toEmail = (item.payload as any)?.toEmail;
                const toName  = (item.payload as any)?.toName;
                return (
                  <React.Fragment key={item.id}>
                    <tr style={{ background: i % 2 === 0 ? 'var(--btn-user-form)' : 'transparent', borderBottom: item.lastError && isExpanded ? 'none' : '1px solid var(--border-input)' }}>
                      <Td>
                        <div style={{ color: 'var(--text-color)', fontWeight: 600 }}>{TYPE_LABEL[item.type] ?? item.type}</div>
                        {(toName || toEmail) && (
                          <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>
                            {toName && <span>{toName}</span>}
                            {toName && toEmail && ' · '}
                            {toEmail && <span>{toEmail}</span>}
                          </div>
                        )}
                        {item.lastError && (
                          <button
                            type="button"
                            onClick={() => toggleExpand(item.id)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', marginTop: 3 }}>
                            <div style={{ color: '#dc2626', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                              <span style={{ fontWeight: 700 }}>{isExpanded ? '▾' : '▸'}</span>
                              <span style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                {item.lastError}
                              </span>
                            </div>
                          </button>
                        )}
                      </Td>
                      <Td>
                        <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {st.label}
                        </span>
                      </Td>
                      <Td>{item.attempts}/{item.maxAttempts}</Td>
                      <Td style={{ whiteSpace: 'nowrap' }}>{fmt(item.createdAt)}</Td>
                      <Td style={{ whiteSpace: 'nowrap' }}>{item.nextAttemptAt && item.status === 'pending' ? fmt(item.nextAttemptAt) : '—'}</Td>
                      <Td>
                        <div className="flex items-center gap-[4px]">
                          {(item.status === 'failed' || item.status === 'pending') && (
                            <button
                              type="button"
                              onClick={() => handleRetry(item.id)}
                              disabled={isRetrying || isDeleting}
                              className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[2px] px-[8px] rounded-[5px] text-[11px] cursor-pointer hover:bg-[var(--btn-user-hover)] disabled:opacity-50">
                              {isRetrying ? '…' : 'Retry'}
                            </button>
                          )}
                          <button
                            type="button"
                            aria-label="Delete"
                            onClick={() => handleDelete(item.id)}
                            disabled={isRetrying || isDeleting}
                            style={{ background: 'rgba(220,38,38,.1)', color: '#dc2626', border: 'none', borderRadius: 5, padding: '2px 7px', fontSize: 13, cursor: 'pointer', lineHeight: 1.4, opacity: (isRetrying || isDeleting) ? 0.4 : 1 }}>
                            {isDeleting ? '…' : '✕'}
                          </button>
                        </div>
                      </Td>
                    </tr>
                    {item.lastError && isExpanded && (
                      <tr style={{ background: i % 2 === 0 ? 'var(--btn-user-form)' : 'transparent', borderBottom: '1px solid var(--border-input)' }}>
                        <td colSpan={6} style={{ padding: '4px 10px 10px 10px' }}>
                          <div style={{
                            background: 'rgba(220,38,38,.07)',
                            border: '1px solid rgba(220,38,38,.2)',
                            borderRadius: 5,
                            padding: '8px 10px',
                            color: '#dc2626',
                            fontSize: 11,
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            lineHeight: 1.5,
                          }}>
                            {item.lastError}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── History tab ───────────────────────────────────────────────────────────────

interface LogItem {
  id: number;
  type: string;
  status: 'success' | 'failed';
  fromEmail: string | null;
  toEmail: string | null;
  toName: string | null;
  payload: Record<string, any> | null;
  errorMessage: string | null;
  createdAt: string;
}

const LOG_STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  success: { bg: 'rgba(5,150,105,.12)',  color: '#059669', label: 'Sent'   },
  failed:  { bg: 'rgba(220,38,38,.12)', color: '#dc2626', label: 'Failed' },
};

const LOG_COLS: ColDef<LogItem>[] = [
  {
    key: 'status',
    label: 'Status',
    width: 80,
    filterType: 'select',
    filterOptions: [{ value: 'success', label: 'Sent' }, { value: 'failed', label: 'Failed' }],
    filterExtract: r => r.status,
    render: r => {
      const st = LOG_STATUS_STYLE[r.status] ?? LOG_STATUS_STYLE.success;
      return (
        <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {st.label}
        </span>
      );
    },
  },
  {
    key: 'type',
    label: 'Type',
    filterType: 'text',
    filterExtract: r => TYPE_LABEL[r.type] ?? r.type,
    render: r => <span style={{ fontWeight: 600 }}>{TYPE_LABEL[r.type] ?? r.type}</span>,
  },
  {
    key: 'toName',
    label: 'To',
    filterType: 'text',
    filterExtract: r => `${r.toName ?? ''} ${r.toEmail ?? ''}`,
    render: r => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.toName ?? '—'}</div>
        {r.toEmail && <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{r.toEmail}</div>}
      </div>
    ),
  },
  {
    key: 'fromEmail',
    label: 'From',
    filterType: 'text',
    render: r => r.fromEmail ?? '—',
  },
  {
    key: 'info',
    label: 'Report',
    sortable: false,
    render: r => {
      const p = r.payload;
      if (!p) return '—';
      if (r.type === 'pendingReport') return `${p.employeeName ?? ''} · ${p.weekStart}–${p.weekEnd}`;
      if (r.type === 'rejectedReport') return `${p.reportDate ?? ''} ${p.startTime ?? ''}–${p.endTime ?? ''}`;
      if (r.type === 'invite_user') return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || '—';
      return '—';
    },
  },
  {
    key: 'createdAt',
    label: 'Date',
    width: 140,
    filterType: 'daterange',
    filterExtract: r => r.createdAt,
    sortExtract: r => new Date(r.createdAt).getTime(),
    render: r => fmt(r.createdAt),
  },
];

function HistoryTab() {
  const [items, setItems] = useState<LogItem[]>([]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('mail-log');
      setItems(data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DataTable
      rows={items}
      columns={LOG_COLS}
      getKey={r => r.id}
      defaultSortKey="createdAt"
      storageKey="mail-log-table"
      exportable={false}
      emptyText="No mail history yet"
      defaultPageSize={25}
    />
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex-1 rounded-[6px] bg-[var(--btn-user-form)] px-[12px] py-[8px]" style={{ border: '1px solid var(--border-input)' }}>
      <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-[30px] text-[var(--text-color-calendar-week)] text-[13px]">
      Loading…
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="text-center py-[30px] text-[13px] text-[var(--text-color-calendar-week)]">
      {text}
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text-color)', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {children}
    </th>
  );
}

function Td({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <td style={{ padding: '7px 10px', color: 'var(--text-color-calendar-week)', verticalAlign: 'middle', ...style }}>
      {children}
    </td>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

export { QueueTab, HistoryTab };

export default function MailQueuePanel({ view }: { view: 'queue' | 'history' }) {
  return view === 'queue' ? <QueueTab /> : <HistoryTab />;
}
